import { useEffect } from "react";
import { useLoaderData } from "@remix-run/react";
import { useCurrentUser } from "@core/hooks/useCurrentUser";
import { LoginForm } from "@core/components/login-form";
import { json, LinksFunction, LoaderFunctionArgs } from "@remix-run/node";
import { useTranslation } from "react-i18next";
import { getQueryParams } from "@core/helpers";
import { getApi } from "@core/api";
import { resolvePostLoginDestination, sanitizeReturnUrl } from "@/overrides/site/utils/site-auth-redirect";
import { LoginRouteConfigSection } from "@/apidtos";

export const links: LinksFunction = () => [
  { rel: "stylesheet", href: "/spg-theme-assets/spg-login-style.css" },
];

export async function loader(args: LoaderFunctionArgs) {
  const api = await getApi(args.request);
  const queryParams = getQueryParams(args.request);
  const config = await api["/config"]();
  const loginConfig = config.Login as LoginRouteConfigSection;
  const returnUrl = sanitizeReturnUrl(queryParams.get("returnUrl"));
  const redirectTo = resolvePostLoginDestination(loginConfig, returnUrl);

  return json({
    registered: queryParams.get("registered") === "true",
    hideLayout: true,
    redirectTo,
  });
}

export default function LoginPage() {
  const { user, isLoading } = useCurrentUser();
  const { registered, redirectTo } = useLoaderData<typeof loader>();
  const { t } = useTranslation();

  useEffect(() => {
    if (!isLoading && user) {
      // Hard navigation so auth-dependent loaders refresh after session is present.
      window.location.assign(redirectTo);
    }
  }, [user, isLoading, redirectTo]);

  if (isLoading) {
    return null;
  }

  return (
    <div className="spg-login-page" data-testid="login-page">
      <div className="spg-login-bg" />
      <div className="spg-login-gradient" />
      <div className="spg-login-content">
        <h1 className="spg-login-title">{t("ui.signIn", "Sign in")}</h1>
        <p className="spg-login-subtitle">
          {t(
            "ui.login.signInSubtitle",
            "to access the broadest, most innovative compounded product menu in the market"
          )}
        </p>
        {registered && (
          <div className="spg-login-banner">
            {t(
              "ui.login.registeredMessage",
              "You have successfully registered. Please sign in to continue."
            )}
          </div>
        )}
        <div className="spg-login-form-wrap">
          <LoginForm redirectTo={redirectTo} />
        </div>
      </div>
    </div>
  );
}
